import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Badge, IconButton, Box } from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import { useChatContext } from '../../context/ChatContext';
import { useAuth } from '../../context/AuthContext';
import '../../styles/ChatBubble.css';

export default function ChatBubble() {
  const { user } = useAuth();
  const { unreadCount } = useChatContext();
  const navigate = useNavigate();
  const location = useLocation();

  const [position, setPosition] = useState({ x: window.innerWidth - 100, y: window.innerHeight - 100 });
  const [isDragging, setIsDragging] = useState(false);
  const [hasMoved, setHasMoved] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showDeleteZone, setShowDeleteZone] = useState(false);
  const [isInDeleteZone, setIsInDeleteZone] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);

  const bubbleRef = useRef(null);

  // Reset dismissed state when new messages arrive
  useEffect(() => {
    if (unreadCount > previousUnreadCount && dismissed) {
      setDismissed(false); // Re-show bubble if new messages come in
    }
    setPreviousUnreadCount(unreadCount);
  }, [unreadCount]);

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setHasMoved(false);
    setShowDeleteZone(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;
      
      const distanceMoved = Math.sqrt(
        Math.pow(newX - position.x, 2) + Math.pow(newY - position.y, 2)
      );
      
      if (distanceMoved > 5) {
        setHasMoved(true);
      }
      
      setPosition({ x: newX, y: newY });

      const deleteZoneX = window.innerWidth / 2 - 50;
      const deleteZoneY = window.innerHeight - 100;
      const inZone = 
        newX > deleteZoneX - 50 && 
        newX < deleteZoneX + 100 && 
        newY > deleteZoneY - 50;
      
      setIsInDeleteZone(inZone);
    }
  };

  const handleMouseUp = () => {
    if (isDragging) {
      setIsDragging(false);
      setShowDeleteZone(false);

      if (isInDeleteZone) {
        setDismissed(true);
      }

      setIsInDeleteZone(false);
    }
  };

  const handleClick = (e) => {
    if (!hasMoved && !isDragging) {
      navigate('/chat');
    }
    setHasMoved(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart, position]);

  // Hide bubble if: not logged in, on chat page, dismissed, OR no unread messages
  if (!user || location.pathname === '/chat' || dismissed || unreadCount === 0) {
    return null;
  }

  return (
    <>
      {showDeleteZone && (
        <Box
          className={`chat-bubble-delete-zone ${isInDeleteZone ? 'active' : ''}`}
          sx={{
            position: 'fixed',
            bottom: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            width: 100,
            height: 100,
            borderRadius: '50%',
            backgroundColor: isInDeleteZone ? '#ff4444' : '#666',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.3s ease',
            zIndex: 9999,
            opacity: 0.8,
          }}
        >
          <CloseIcon sx={{ fontSize: 40, color: 'white' }} />
        </Box>
      )}

      <Box
        ref={bubbleRef}
        className={`chat-bubble ${isDragging ? 'dragging' : ''}`}
        sx={{
          position: 'fixed',
          left: position.x,
          top: position.y,
          zIndex: 10000,
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
        }}
        onMouseDown={handleMouseDown}
        onClick={handleClick}
      >
        <Badge 
          badgeContent={unreadCount} 
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: '#ff4444',
              color: 'white',
              fontSize: '14px',
              fontWeight: 'bold',
              minWidth: '22px',
              height: '22px',
              borderRadius: '11px',
              border: '2px solid white',
            }
          }}
        >
          <IconButton
            sx={{
              backgroundColor: '#667eea',
              color: 'white',
              width: 60,
              height: 60,
              '&:hover': {
                backgroundColor: '#764ba2',
              },
              boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
              pointerEvents: 'none',
            }}
          >
            <ChatIcon sx={{ fontSize: 30 }} />
          </IconButton>
        </Badge>
      </Box>
    </>
  );
}